import { X, Check, Thermometer, Volume2, ShieldCheck, Sparkles, TrendingUp, Wrench, Wind, Ruler, Phone, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const ATTRIBUTES = [{
  Icon: Ruler,
  name: "Panel thickness",
  single: "0.4 mm steel",
  insulated: "42 mm — steel + foam core"
}, {
  Icon: Thermometer,
  name: "Thermal performance",
  single: "Conducts heat directly",
  insulated: "R-2.0 — up to 8°C cooler"
}, {
  Icon: Volume2,
  name: "Acoustic dampening",
  single: "Drum-like, amplifies noise",
  insulated: "27 dB reduction (STC 27+)"
}, {
  Icon: ShieldCheck,
  name: "Dent &amp; impact resistance",
  single: "Dents from bikes / balls / trolleys",
  insulated: "Stiff sandwich — rebounds cleanly"
}, {
  Icon: Wind,
  name: "High-wind / storm rating",
  single: "Bows under sustained pressure",
  insulated: "Bonded panels stay rigid"
}, {
  Icon: TrendingUp,
  name: "Resale &amp; appraisal impact",
  single: "Often flagged at inspection",
  insulated: "Adds $4–8k average value"
}, {
  Icon: Wrench,
  name: "Typical service life",
  single: "~7 years before fade / dents",
  insulated: "15+ years with annual service"
}, {
  Icon: Sparkles,
  name: "Interior finish",
  single: "Raw steel ribs, no paint",
  insulated: "Pre-painted matt white inside"
}];
export default function InsulatedComparison() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* Ambient radial */}
      <div aria-hidden className="absolute inset-0" style={{
      background: `radial-gradient(circle at 50% 0%, ${NAVY_LIGHT}66 0%, ${NAVY} 60%), radial-gradient(circle at 50% 100%, ${GOLD}10 0%, transparent 50%)`
    }} />

      {/* Grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")"
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />

      {/* Watermark */}
      <div aria-hidden className="absolute pointer-events-none select-none" style={{
      left: "-30px",
      top: "40px",
      fontFamily: FONT_HEAD,
      fontSize: "240px",
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: "-0.05em",
      color: `${GOLD}12`
    }}>
        07
      </div>

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-14 max-w-3xl">
          <div className="mb-5 flex items-center gap-3">
            <div aria-hidden className="h-px w-12" style={{
            backgroundColor: GOLD
          }} />
            <span style={{
            fontFamily: FONT_HEAD,
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: GOLD
          }}>
              07 · Head-to-Head
            </span>
          </div>

          <h2 style={{
          fontFamily: FONT_HEAD,
          fontWeight: 800,
          fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
          lineHeight: 1.08,
          letterSpacing: "-0.025em",
          color: "#ffffff",
          marginBottom: "20px"
        }}>
            Single-skin door vs
            <br />
            <span style={{
            color: GOLD
          }}>insulated, side-by-side</span>.
          </h2>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "17px",
          lineHeight: 1.65,
          color: "rgba(255,255,255,0.72)",
          maxWidth: "640px"
        }}>
            Eight attributes that decide whether your next door pays for
            itself — or just looks the same as the one you replaced.
          </p>
        </div>

        {/* VS battle card */}
        <div className="relative">
          {/* Floating VS badge (desktop) */}
          <div aria-hidden className="hidden lg:flex absolute items-center justify-center rounded-full" style={{
          left: "50%",
          top: "70px",
          transform: "translateX(-50%)",
          width: 64,
          height: 64,
          background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`,
          color: NAVY,
          fontFamily: FONT_HEAD,
          fontWeight: 900,
          fontSize: "18px",
          letterSpacing: "-0.02em",
          zIndex: 2,
          boxShadow: `0 12px 32px ${GOLD}55, 0 0 0 6px ${NAVY}`
        }}>
            VS
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            {/* LEFT — Single-skin (muted) */}
            <div className="relative rounded-2xl overflow-hidden" style={{
            backgroundColor: `${NAVY_DEEP}AA`,
            border: `1px solid rgba(255,255,255,0.08)`
          }}>
              {/* Header */}
              <div className="p-7 md:p-8 border-b" style={{
              borderColor: "rgba(255,255,255,0.06)"
            }}>
                <div style={{
                fontFamily: FONT_HEAD,
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.4)",
                marginBottom: "8px"
              }}>
                  The old way
                </div>
                <h3 style={{
                fontFamily: FONT_HEAD,
                fontWeight: 800,
                fontSize: "1.65rem",
                lineHeight: 1.15,
                color: "rgba(255,255,255,0.85)",
                letterSpacing: "-0.025em",
                marginBottom: "8px"
              }}>
                  Single-skin door
                </h3>
                <p style={{
                fontFamily: FONT_BODY,
                fontSize: "13.5px",
                color: "rgba(255,255,255,0.55)",
                lineHeight: 1.5
              }}>
                  0.4 mm steel sheet. No core. What most Sydney garages still
                  have.
                </p>
              </div>

              {/* Rows */}
              <ul>
                {ATTRIBUTES.map(({
                Icon,
                name,
                single
              }, i) => <li key={name} className="px-7 md:px-8 py-4 border-b" style={{
                borderColor: i === ATTRIBUTES.length - 1 ? "transparent" : "rgba(255,255,255,0.05)"
              }}>
                    <div className="flex items-center gap-3 mb-1.5">
                      <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                        <Icon size={13} color="rgba(255,255,255,0.4)" strokeWidth={2} />
                      </span>
                      <span style={{
                    fontFamily: FONT_HEAD,
                    fontSize: "11px",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.5)"
                  }} dangerouslySetInnerHTML={{
                    __html: name
                  }} />
                    </div>
                    <div className="flex items-start gap-2.5 pl-6">
                      <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-md flex-shrink-0 mt-0.5" style={{
                    width: 18,
                    height: 18,
                    backgroundColor: "#E5535B22",
                    border: "1px solid #E5535B44"
                  }}>
                        <X size={10} color="#E5535B" strokeWidth={3} />
                      </span>
                      <span style={{
                    fontFamily: FONT_BODY,
                    fontSize: "13.5px",
                    color: "rgba(255,255,255,0.6)",
                    lineHeight: 1.5
                  }}>
                        {single}
                      </span>
                    </div>
                  </li>)}
              </ul>
            </div>

            {/* RIGHT — Insulated (highlighted) */}
            <div className="relative rounded-2xl overflow-hidden" style={{
            background: `linear-gradient(180deg, ${NAVY_LIGHT}55 0%, ${NAVY_DEEP}AA 100%)`,
            border: `2px solid ${GOLD}55`,
            boxShadow: `0 30px 70px rgba(0,0,0,0.4), 0 0 0 1px ${GOLD}33`
          }}>
              {/* Top gold edge */}
              <div aria-hidden className="absolute top-0 left-0 right-0 h-1.5" style={{
              background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`
            }} />

              {/* Featured stamp */}
              <div className="absolute" style={{
              top: "24px",
              right: "24px",
              padding: "4px 10px",
              borderRadius: "999px",
              background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`,
              color: NAVY,
              fontFamily: FONT_HEAD,
              fontSize: "10px",
              fontWeight: 900,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              boxShadow: `0 4px 12px ${GOLD}66`
            }}>
                ★ Recommended
              </div>

              {/* Header */}
              <div className="p-7 md:p-8 border-b relative" style={{
              borderColor: `${GOLD}33`
            }}>
                <div style={{
                fontFamily: FONT_HEAD,
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: GOLD,
                marginBottom: "8px"
              }}>
                  The Gold standard
                </div>
                <h3 style={{
                fontFamily: FONT_HEAD,
                fontWeight: 800,
                fontSize: "1.65rem",
                lineHeight: 1.15,
                color: "#ffffff",
                letterSpacing: "-0.025em",
                marginBottom: "8px"
              }}>
                  Insulated door
                </h3>
                <p style={{
                fontFamily: FONT_BODY,
                fontSize: "13.5px",
                color: "rgba(255,255,255,0.78)",
                lineHeight: 1.5
              }}>
                  42 mm sandwich panel. Foamed polyurethane core. What
                  modern Sydney homes are upgrading to.
                </p>
              </div>

              {/* Rows */}
              <ul>
                {ATTRIBUTES.map(({
                Icon,
                name,
                insulated
              }, i) => <li key={name} className="px-7 md:px-8 py-4 border-b" style={{
                borderColor: i === ATTRIBUTES.length - 1 ? "transparent" : `${GOLD}1A`
              }}>
                    <div className="flex items-center gap-3 mb-1.5">
                      <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                        <Icon size={13} color={GOLD} strokeWidth={2.25} />
                      </span>
                      <span style={{
                    fontFamily: FONT_HEAD,
                    fontSize: "11px",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: GOLD
                  }} dangerouslySetInnerHTML={{
                    __html: name
                  }} />
                    </div>
                    <div className="flex items-start gap-2.5 pl-6">
                      <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-md flex-shrink-0 mt-0.5" style={{
                    width: 18,
                    height: 18,
                    backgroundColor: `${GOLD}25`,
                    border: `1px solid ${GOLD}55`
                  }}>
                        <Check size={10} color={GOLD} strokeWidth={3} />
                      </span>
                      <span style={{
                    fontFamily: FONT_BODY,
                    fontSize: "14px",
                    color: "rgba(255,255,255,0.92)",
                    lineHeight: 1.5,
                    fontWeight: 600
                  }}>
                        {insulated}
                      </span>
                    </div>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "15px",
          lineHeight: 1.6,
          color: "rgba(255,255,255,0.7)",
          maxWidth: "560px"
        }}>
            <span style={{
            color: GOLD,
            fontFamily: FONT_HEAD,
            fontWeight: 700
          }}>
              The honest answer:
            </span>{" "}
            single-skin is fine for a shed or rural shop. For a modern Sydney
            home — especially with a room above the garage — insulated pays
            for itself in comfort and resale.
          </p>

          <a href="tel:0485000933" className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full transition-all hover:scale-[1.02] flex-shrink-0" style={{
          backgroundColor: GOLD,
          color: NAVY,
          fontFamily: FONT_HEAD,
          fontWeight: 700,
          fontSize: "14px",
          boxShadow: `0 8px 24px ${GOLD}33`
        }}>
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <Phone size={16} strokeWidth={2.5} />
            </span>
            Quote my upgrade
            <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
              <ArrowRight size={14} strokeWidth={2.5} />
            </span>
          </a>
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />
    </section>;
}